import React from 'react';
import { AutocompleteDictionary } from './autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import {
    accessibleInforProfReq,
    accommodationStatus,
    acuity,
    arrivalModes,
    attendanceSource,
    chiefComplaint,
    comorbiditis,
    dischargeDestination,
    dischargeFollowUp,
    dischargeInfoGiven,
    dischargeStatus,
    getEcdsVersion,
    injuryActivityStatus,
    injuryActivityType,
    injuryDrugAlcohol,
    injuryIntent,
    injuryMechanism,
    interpreterLanguage,
    placeOfInjury,
    referredToService,
    safeguarding,
    sicknessAbsenceCategory,
    socialAndPersonalDict,
    spokenLanguage,
} from '../../store/reducers/dicts';
import AgyleDictionaryField from '../../components/ui/selector/agyleDictionaryField';
import Chip from '@mui/material/Chip';
import ECDSDictionaryField from '../../components/ui/selector/ECDSDictionaryField';
import { getTriageButtonColorMatrix } from '../../pages/ward/forms/triage/triageUtils';

// DischargeECDS Status
function DischargeStatus(props) {
    const dict = useSelector(dischargeStatus);
    return (
        <ECDSDictionaryField
            renderLocalOption={(props, option, state) => renderEcdsOptionLine(props, option, state, null, 'snomedPreferredTerm')}
            getOptionLabel={(option) => option?.snomedPreferredTerm || option?.__description}
            filterOptions={filterOptions}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// Safeguarding
function Safeguarding(props) {
    const dispatch = useDispatch();

    // ECDS version
    const version = dispatch(getEcdsVersion(props.admitDate));
    const ecdsVersionFilter = encodeURIComponent(`ecdsVersion eq '${version}' and deactivated ne true`);

    const [grouping, setGrouping] = React.useState(!props.showIndicatedFlag);

    const getDictionaryURL = (filter) => {
        if (props.showIndicatedFlag) {
            let newFilter = `(${props.showIndicatedFlag} eq true)`;
            if (filter !== '') {
                setGrouping(true);
                newFilter = filter;
            } else {
                setGrouping(false);
            }
            return `/$dict/EcdsSafeguarding?$filter=((${ecdsVersionFilter}) and (${encodeURIComponent(newFilter)}))&$sort=sort1,sort2,sort3,sort4`;
        }
        let filterText = '';
        if (filter) filterText = ` and (${encodeURIComponent(filter)})`;
        return `/$dict/EcdsSafeguarding?$filter=((${ecdsVersionFilter})${filterText})&$sort=sort1,sort2,sort3,sort4`;
    };

    return (
        <AutocompleteDictionary
            showColumnText={'__description'}
            multiple
            searchColumnName={'description'}
            groupBy={grouping ? (opt) => opt.group : null}
            dictionaryURL={getDictionaryURL}
            extended
            {...props}
        />
    );
}

function SafeguardingECDS(props) {
    let dict = useSelector(safeguarding);
    if (props.ecdsVersion) dict = dict.filter((d) => d.ecdsVersion === props.ecdsVersion);

    return (
        <AgyleDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={filterOptions}
            options={dict}
            groupBy={(opt) => opt.group}
            isMultiple
            {...props}
        />
    );
}

// DischargeED Destination
function SafeguardingTemplate(props) {
    const dict = useSelector(safeguarding);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={filterOptions}
            options={dict}
            alwaysGroup
            multiple
            {...props}
        />
    );
}

// DischargeED Destination
function DischargeDestination(props) {
    const dict = useSelector(dischargeDestination);

    return (
        <ECDSDictionaryField
            renderLocalOption={(props, option, state) => renderEcdsOptionLine(props, option, state, null, 'snomedPreferredTerm')}
            getOptionLabel={(option) => option?.snomedPreferredTerm || option?.__description}
            filterOptions={filterOptions}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// DischargeECDS Follow Up
function DischargeFollowUp(props) {
    const dict = useSelector(dischargeFollowUp);

    return (
        <ECDSDictionaryField
            renderLocalOption={(props, option, state) => renderEcdsOptionLine(props, option, state, null, 'snomedPreferredTerm')}
            getOptionLabel={(option) => option?.snomedPreferredTerm || option?.__description}
            filterOptions={filterOptions}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// DischargeECDS Information Given
function DischargeInfoGiven(props) {
    const dict = useSelector(dischargeInfoGiven);

    return <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} {...props} />;
}

// Referred to Other Service
function ReferredToService(props) {
    const dict = useSelector(referredToService);

    return (
        <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} alwaysGroup {...props} />
    );
}

// Comorbidity
function Comorbidity(props) {
    const dict = useSelector(comorbiditis);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={filterOptions}
            options={dict}
            alwaysGroup
            isMultiple
            {...props}
        />
    );
}

// Place of injury
function PlaceOfInjury(props) {
    const dict = useSelector(placeOfInjury);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={(opt, state) => filterOptions(opt, state, ['__description', 'searchTerms'])}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// Injury Intent
function InjuryIntent(props) {
    const dict = useSelector(injuryIntent);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={(opt, state) => filterOptions(opt, state, ['__description', 'searchTerms'])}
            options={dict}
            {...props}
        />
    );
}

// Injury Activity Status
function InjuryActivityStatus(props) {
    const dict = useSelector(injuryActivityStatus);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={(opt, state) => filterOptions(opt, state, ['__description', 'searchTerms'])}
            options={dict}
            {...props}
        />
    );
}

// Injury Activity Type
function InjuryActivityType(props) {
    const dict = useSelector(injuryActivityType);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={(opt, state) => filterOptions(opt, state, ['__description', 'searchTerms'])}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// Injury Mechanism
function InjuryMechanism(props) {
    const dict = useSelector(injuryMechanism);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={(opt, state) => filterOptions(opt, state, ['__description', 'searchTerms'])}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// Injury Drug and Alcohol
function InjuryDrugAlcohol(props) {
    const dict = useSelector(injuryDrugAlcohol);

    return (
        <ECDSDictionaryField
            renderLocalOption={renderEcdsOptionLine}
            filterOptions={(opt, state) => filterOptions(opt, state, ['__description', 'searchTerms'])}
            options={dict}
            alwaysGroup
            isMultiple
            {...props}
        />
    );
}

// Arrival Mode
function ArrivalMode(props) {
    const dict = useSelector(arrivalModes);

    return <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} {...props} />;
}

// Attendance Source
function AttendanceSource(props) {
    const dict = useSelector(attendanceSource);

    return (
        <ECDSDictionaryField
            renderLocalOption={(props, option, state) => renderEcdsOptionLine(props, option, state, null, 'snomedPreferredTerm')}
            getOptionLabel={(option) => option?.snomedPreferredTerm || option?.__description}
            filterOptions={filterOptions}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

// Acuity
function Acuity(props) {
    const dict = useSelector(acuity);

    return <ECDSDictionaryField renderLocalOption={renderAcuityEcdsOptionLine} filterOptions={filterOptions} options={dict} {...props} />;
}

// Chief Of Complaint
function ChiefOfComplaint(props) {
    const dispatch = useDispatch();

    // ECDS version
    const version = dispatch(getEcdsVersion(props.admitDate));
    const ecdsVersionFilter = encodeURIComponent(`ecdsVersion eq '${version}' and deactivated ne true`);

    const getDictionaryURL = (filter) => {
        if (props.fromAdmit) {
            return `/$dict/EcdsChiefOfComplaint?$filter=((${ecdsVersionFilter}) and (${encodeURIComponent(filter)}))&$sort=sort1,sort2,sort3,sort4`;
        }
        return `/$dict/EcdsChiefOfComplaint?$filter=((${ecdsVersionFilter}) and (${encodeURIComponent(filter)}))&$sort=sort1,sort2,sort3,sort4`;
    };

    return (
        <AutocompleteDictionary
            getOptionLabel={(option) => getOptionLabelToCC(version, option)}
            multiSearchColumnName={['snomedCode', 'description', 'searchTerms', 'mapsToSelf', 'snomedPreferredTerm']}
            renderText={version === 'V3' ? 'parentTerm' : 'snomedPreferredTerm'}
            groupBy={(opt) => opt.group}
            dictionaryURL={getDictionaryURL}
            {...props}
        />
    );
}

// Chief Of Complaint
function ChiefOfComplaintECDS(props) {
    const dispatch = useDispatch();
    const dict = useSelector(chiefComplaint);
    const version = dispatch(getEcdsVersion(props.admitDate));

    return (
        <ECDSDictionaryField
            getOptionLabel={(option) => getOptionLabelToCC(version, option)}
            renderLocalOption={(props, option, state) =>
                renderEcdsOptionLine(props, option, state, null, version === 'V3' ? 'parentTerm' : 'snomedPreferredTerm')
            }
            filterOptions={(opt, state) => filterOptions(opt, state, ['description', 'searchTerms', 'mapsToSelf', 'snomedPreferredTerm'])}
            options={dict}
            alwaysGroup
            {...props}
        />
    );
}

const getOptionLabelToCC = (ecdsVersion, option) => {
    return option?.ecdsVersion === 'V3' ? option?.parentTerm : option?.snomedPreferredTerm;
};

// Accommodation Status
function AccommodationStatus(props) {
    const dict = useSelector(accommodationStatus);

    return (
        <ECDSDictionaryField
            renderLocalOption={props.noCodes ? null : renderEcdsOptionLine}
            filterOptions={filterOptions}
            options={dict}
            {...props}
        />
    );
}

// Preferred spoken language
function SpokenLanguage(props) {
    const dict = useSelector(spokenLanguage);

    return (
        <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} alwaysGroup {...props} />
    );
}

// Accessible Information Professional Required Code
function AccessibleInforProfReq(props) {
    const dict = useSelector(accessibleInforProfReq);

    return <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} {...props} />;
}

// Interpreter Language
function InterpreterLanguage(props) {
    const dict = useSelector(interpreterLanguage);

    return (
        <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} alwaysGroup {...props} />
    );
}

// Sickness Absence Category
function SicknessAbsenceCategory(props) {
    const dict = useSelector(sicknessAbsenceCategory);

    return (
        <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} alwaysGroup {...props} />
    );
}

// Sickness Absence Category
function SocialAndPersonal(props) {
    const dict = useSelector(socialAndPersonalDict);

    return (
        <ECDSDictionaryField renderLocalOption={renderEcdsOptionLine} filterOptions={filterOptions} options={dict} alwaysGroup {...props} />
    );
}

export {
    DischargeStatus,
    SafeguardingECDS,
    Safeguarding,
    DischargeDestination,
    DischargeFollowUp,
    DischargeInfoGiven,
    ReferredToService,
    Comorbidity,
    PlaceOfInjury,
    InjuryIntent,
    InjuryActivityStatus,
    InjuryActivityType,
    InjuryMechanism,
    InjuryDrugAlcohol,
    ArrivalMode,
    AttendanceSource,
    Acuity,
    ChiefOfComplaint,
    ChiefOfComplaintECDS,
    AccommodationStatus,
    SpokenLanguage,
    AccessibleInforProfReq,
    InterpreterLanguage,
    SicknessAbsenceCategory,
    SocialAndPersonal,
    SafeguardingTemplate,
};

export const renderEcdsOptionLine = (props, option, state, _ep, showText = '__description') => {
    return (
        <li {...props}>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
            >
                <Chip size="small" style={{ marginRight: 5 }} label={option?.snomedCode} />
                <div>{option[showText]}</div>
            </div>
        </li>
    );
};

const renderAcuityEcdsOptionLine = (props, option, state, _ep, showText = '__description') => {
    const color = getTriageButtonColorMatrix(option);

    return (
        <li {...props}>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
            >
                <Chip size="small" style={{ marginRight: 5, backgroundColor: color, color: '#000000', minWidth: 25 }} />
                <div>{option[showText]}</div>
            </div>
        </li>
    );
};

export function filterOptions(options, { inputValue }, opt = ['__description']) {
    return options.filter((option) => opt.some((field) => option[field]?.toLowerCase()?.includes(inputValue.toLowerCase())));
}
